import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  MailOutlined,
  PhoneOutlined,
  EnvironmentOutlined
} from '@ant-design/icons';
import { FaFacebook, FaTwitter, FaInstagram, FaYoutube, FaWhatsapp, FaSnapchat, FaPhoneAlt } from 'react-icons/fa';
import './Footer.css';
import { useSiteSettings } from '../hooks/useSiteSettings';

const Footer = () => {
  const {
    site_name,
    site_logo,
    siteDescription,
    contactEmail,
    contactPhone,
    address,
    facebookUrl,
    twitterUrl,
    instagramUrl,
    youtubeUrl,
    tiktokUrl,
    whatsappUrl,
    callUrl,
    snapchatUrl,
    navLinks
  } = useSiteSettings();

  const [sections, setSections] = useState([]);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_URL || ''}/api/sections/active`)
      .then(res => {
        if (!res.ok) throw new Error('تعذر جلب الأقسام');
        return res.json();
      })
      .then(data => {
        setSections(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        // لا نعرض أي رسالة خطأ، يتفرغ العمود تلقائياً
      });
  }, []);

  const currentYear = new Date().getFullYear();

  const contactInfo = [
    { icon: <MailOutlined />, label: 'البريد الإلكتروني', value: contactEmail },
    { icon: <PhoneOutlined />, label: 'الهاتف', value: contactPhone },
    { icon: <EnvironmentOutlined />, label: 'العنوان', value: address },
  ].filter(item => item.value && item.value.trim());

  const quickLinks = Array.isArray(navLinks) ? navLinks : [];

  return (
    <footer className="main-footer">
      <div className="footer-main">
        <div className="container">
          <div className="footer-grid">
            <div className="footer-brand">
              <div className="footer-logo-section">
                {site_logo && (
                  <img
                    src={site_logo}
                    alt={`شعار ${site_name || 'مؤسسة ثراء الخير للمقاولات والديكورات'}`}
                    className="footer-logo"
                  />
                )}
                <div className="footer-brand-text">
                  {site_name && <h3 className="footer-title">{site_name}</h3>}
                  {/* استغلال العنصر الفارغ لدعم الـ SEO دون التأثير على التصميم */}
                  <p className="footer-subtitle" >للزجاج السكريت والالمنيوم استركتشر والديكورات الخشبية </p>
                </div>
              </div>
              <p className="footer-description">
                {/* استخدام وصف افتراضي مليء بالكلمات المفتاحية في حال كان الوصف القادم من الإعدادات فارغاً */}
                {siteDescription || 'نقدم أفضل خدمات المقاولات، التشطيبات المعمارية، تصميم وتنفيذ الديكورات، أعمال الدهانات، وتركيب بديل الخشب والرخام بأعلى معايير الجودة والاحترافية.'}
              </p>
              {(facebookUrl || twitterUrl || instagramUrl || youtubeUrl || whatsappUrl || callUrl || snapchatUrl) && (
                <div className="footer-social">
                  {facebookUrl && (
                    <a href={facebookUrl} target="_blank" rel="noopener noreferrer" className="social-link facebook" aria-label="فيسبوك مقاولات وديكور">
                      <FaFacebook />
                    </a>
                  )}
                  {twitterUrl && (
                    <a href={twitterUrl} target="_blank" rel="noopener noreferrer" className="social-link twitter" aria-label="تويتر">
                      <FaTwitter />
                    </a>
                  )}
                  {instagramUrl && (
                    <a href={instagramUrl} target="_blank" rel="noopener noreferrer" className="social-link instagram" aria-label="انستغرام اعمال التشطيبات">
                      <FaInstagram />
                    </a>
                  )}
                  {youtubeUrl && (
                    <a href={youtubeUrl} target="_blank" rel="noopener noreferrer" className="social-link youtube" aria-label="قناة يوتيوب للاعمال">
                      <FaYoutube />
                    </a>
                  )}
                  {tiktokUrl && (
                    <a href={tiktokUrl} target="_blank" rel="noopener noreferrer" className="social-link tiktok" aria-label="تيك توك ديكورات">
                      <i className="fab fa-tiktok" />
                    </a>
                  )}
                  {whatsappUrl && (
                    <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" className="social-link whatsapp" aria-label="تواصل معنا عبر واتساب للمقاولات">
                      <FaWhatsapp />
                    </a>
                  )}
                  {callUrl && (
                    <a href={callUrl} target="_blank" rel="noopener noreferrer" className="social-link call" aria-label="اتصل بمؤسسة المقاولات">
                      <FaPhoneAlt />
                    </a>
                  )}
                  {snapchatUrl && (
                    <a href={snapchatUrl} target="_blank" rel="noopener noreferrer" className="social-link snapchat" aria-label="سناب شات">
                      <FaSnapchat />
                    </a>
                  )}
                </div>
              )}
            </div>

            {quickLinks.length > 0 && (
              <div className="footer-section">
                <h4 className="footer-section-title">روابط سريعة</h4>
                <ul className="footer-links">
                  {quickLinks.map((link) => (
                    <li key={link.to}>
                      <Link to={link.to} className="footer-link" title={`انتقل إلى ${link.label}`}>
                        <span className="link-icon">{link.iconElement}</span>
                        {link.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {sections.length > 0 && (
              <div className="footer-section">
                <h4 className="footer-section-title">خدمات وأقسام المقاولات</h4>
                <ul className="footer-links">
                  {sections.map((section) => (
                    <li key={section.id}>
                      <Link to={`/sections/${section.slug || section.id}`} className="footer-link" title={`عرض أعمال ${section.name}`}>
                        {section.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {contactInfo.length > 0 && (
              <div className="footer-section">
                <h4 className="footer-section-title">معلومات الاتصال</h4>
                <div className="contact-info">
                  {contactInfo.map((contact, index) => (
                    <div key={index} className="contact-item">
                      <div className="contact-icon">{contact.icon}</div>
                      <div className="contact-details">
                        <span className="contact-label">{contact.label}</span>
                        <span className="contact-value">{contact.value}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="footer-bottom">
        <div className="container">
          <div className="footer-bottom-content">
            <div className="copyright">
              © {currentYear} جميع الحقوق محفوظة لـ {site_name || 'مؤسسة ثراء الخير للمقاولات'}
            </div>
            <div className="footer-bottom-links">
              <Link to="/privacy" className="footer-bottom-link">سياسة الخصوصية</Link>
              <Link to="/terms" className="footer-bottom-link">شروط الاستخدام</Link>
              <Link to="/sitemap" className="footer-bottom-link">خريطة الموقع</Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;